import React from 'react';
import { useTranslation } from 'react-i18next';
import { SiteFilter } from '../SiteFilter';
import { CurrencySelector } from '../CurrencySelector';
import { PaymentMethodTypeFilter } from '../PaymentMethodTypeFilter';
import type { FinancialFilters } from './useFinancialFilters';

// ---- date preset helpers ---------------------------------------------------
// All presets are computed in the browser's local timezone and formatted as
// YYYY-MM-DD strings to match the <input type="date"> format and what the API
// expects in date_from / date_to.
type DatePresetKey =
  | 'today'
  | 'yesterday'
  | 'last_week'
  | 'current_week'
  | 'previous_week'
  | 'current_month'
  | 'previous_month';

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Monday-based week start — matches the Italian / European convention used
// across the rest of the reporting UI (ClickHouse toMonday()).
function startOfWeek(d: Date): Date {
  const r = new Date(d);
  const dow = r.getDay(); // 0 = Sunday, 1 = Monday, …
  const diff = dow === 0 ? -6 : 1 - dow;
  r.setDate(r.getDate() + diff);
  r.setHours(0, 0, 0, 0);
  return r;
}

function computePreset(key: DatePresetKey): { from: string; to: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  switch (key) {
    case 'today':
      return { from: toISO(today), to: toISO(today) };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: toISO(y), to: toISO(y) };
    }
    case 'last_week': {
      // Rolling last 7 days, ending today.
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { from: toISO(start), to: toISO(today) };
    }
    case 'current_week':
      return { from: toISO(startOfWeek(today)), to: toISO(today) };
    case 'previous_week': {
      // Previous full calendar week: Mon…Sun of the week before the current one.
      const thisMon = startOfWeek(today);
      const prevMon = new Date(thisMon);
      prevMon.setDate(prevMon.getDate() - 7);
      const prevSun = new Date(prevMon);
      prevSun.setDate(prevSun.getDate() + 6);
      return { from: toISO(prevMon), to: toISO(prevSun) };
    }
    case 'current_month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: toISO(first), to: toISO(today) };
    }
    case 'previous_month': {
      // Previous full calendar month: 1st … last day of the month before current.
      const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastPrev = new Date(firstThis);
      lastPrev.setDate(lastPrev.getDate() - 1);
      const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
      return { from: toISO(firstPrev), to: toISO(lastPrev) };
    }
  }
}

// Reverse lookup: given the current (from, to) values, return the preset key
// they match (or '' for a custom range). Keeps the select in sync after the
// user tweaks the dates manually or navigates via a shared URL.
function detectPreset(from: string, to: string): DatePresetKey | '' {
  const keys: DatePresetKey[] = [
    'today', 'yesterday',
    'last_week', 'current_week', 'previous_week',
    'current_month', 'previous_month',
  ];
  for (const k of keys) {
    const p = computePreset(k);
    if (p.from === from && p.to === to) return k;
  }
  return '';
}

interface FinancialFilterBarProps {
  filters: FinancialFilters;
  showGranularity?: boolean;
  showPaymentType?: boolean;
}

// Shared filter bar used across all Financial sub-tabs. Each control writes
// directly into the URL via the setters exposed by useFinancialFilters(), so
// switching tabs preserves the selection.
export const FinancialFilterBar: React.FC<FinancialFilterBarProps> = ({
  filters,
  showGranularity = true,
  showPaymentType = true,
}) => {
  const { t } = useTranslation();
  const activePreset = detectPreset(filters.dateFrom, filters.dateTo);

  const presets: { key: DatePresetKey; label: string }[] = [
    { key: 'today',          label: t('filters.presetToday',         { defaultValue: 'Today' }) },
    { key: 'yesterday',      label: t('filters.presetYesterday',     { defaultValue: 'Yesterday' }) },
    { key: 'last_week',      label: t('filters.presetLast7Days',     { defaultValue: 'Last 7 Days' }) },
    { key: 'current_week',   label: t('filters.presetCurrentWeek',   { defaultValue: 'Current Week' }) },
    { key: 'previous_week',  label: t('filters.presetPreviousWeek',  { defaultValue: 'Previous Week' }) },
    { key: 'current_month',  label: t('filters.presetCurrentMonth',  { defaultValue: 'Current Month' }) },
    { key: 'previous_month', label: t('filters.presetPreviousMonth', { defaultValue: 'Previous Month' }) },
  ];

  return (
    <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('filters.preset', { defaultValue: 'Preset' })}
        </label>
        <select
          value={activePreset}
          onChange={(e) => {
            const key = e.target.value as DatePresetKey | '';
            if (!key) return;
            const { from, to } = computePreset(key);
            filters.setDateRange(from, to);
          }}
          className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
        >
          <option value="">{t('filters.presetCustom', { defaultValue: 'Custom' })}</option>
          {presets.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.from')}</label>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => filters.setDateFrom(e.target.value)}
          className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.to')}</label>
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => filters.setDateTo(e.target.value)}
          className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
        />
      </div>
      {showGranularity && (
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('filters.granularity', { defaultValue: 'Granularity' })}
          </label>
          <select
            value={filters.granularity}
            onChange={(e) => filters.setGranularity(e.target.value as 'daily' | 'weekly' | 'monthly')}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          >
            <option value="daily">{t('filters.daily', { defaultValue: 'Daily' })}</option>
            <option value="weekly">{t('filters.weekly', { defaultValue: 'Weekly' })}</option>
            <option value="monthly">{t('filters.monthly', { defaultValue: 'Monthly' })}</option>
          </select>
        </div>
      )}
      <SiteFilter selected={filters.siteIds} onChange={filters.setSiteIds} />
      <CurrencySelector selected={filters.currencyMode} onChange={filters.setCurrencyMode} />
      {showPaymentType && (
        <PaymentMethodTypeFilter selected={filters.pmType} onChange={filters.setPmType} />
      )}
    </div>
  );
};
